import {apiBaseUrlDevelopment, apiBaseUrlProduction} from "../config.json";

//see: https://parceljs.org/features/production/
// Original backend URL: https://biobank-editor.i-med.ac.at/api
function joinUrlParts(...parts) {
    return parts
        .filter(Boolean)
        .map((part, index) => {
            const value = String(part);
            return index === 0
                ? value.replace(/\/+$/, "")
                : value.replace(/^\/+|\/+$/g, "");
        })
        .join("/");
}

function resolveApiBaseUrl() {
    const runtimeConfig = (typeof window !== "undefined" && window.__APP_CONFIG__)
        ? window.__APP_CONFIG__
        : null;

    // Preferred for ORDS/APEX static hosting: explicit full API URL.
    if (runtimeConfig && typeof runtimeConfig.apiBaseUrl === "string" && runtimeConfig.apiBaseUrl.trim()) {
        return runtimeConfig.apiBaseUrl.trim().replace(/\/+$/, "");
    }

    // ORDS/APEX runtime config from host page.
    if (runtimeConfig && runtimeConfig.ordsSchema) {
        const ordsBasePath = (runtimeConfig.ordsBasePath || "/ords").trim();
        const hasAbsoluteOrdsBasePath = /^[a-z][a-z0-9+.-]*:\/\//i.test(ordsBasePath);
        const origin = hasAbsoluteOrdsBasePath
            ? ""
            : ((typeof window !== "undefined" && window.location)
                ? window.location.origin
                : "");
        return joinUrlParts(
            origin,
            ordsBasePath,
            runtimeConfig.ordsSchema,
            runtimeConfig.apiModule || "api"
        );
    }

    // Backward-compatible fallback for Node backend deployments.
    return process.env.API_BASE_URL || (
        process.env.NODE_ENV === "development"
            ? apiBaseUrlDevelopment
            : apiBaseUrlProduction
    );
}

let apiBaseUrl;

function getApiBaseUrl() {
    if (!apiBaseUrl) {
        apiBaseUrl = resolveApiBaseUrl();
    }
    return apiBaseUrl;
}

function useHashRouting() {
    const runtimeConfig = (typeof window !== "undefined" && window.__APP_CONFIG__)
        ? window.__APP_CONFIG__
        : null;
    return !!(runtimeConfig && runtimeConfig.routerMode === "hash");
}

function isOrdsDeployment() {
    const runtimeConfig = (typeof window !== "undefined" && window.__APP_CONFIG__)
        ? window.__APP_CONFIG__
        : null;
    if (runtimeConfig && runtimeConfig.ordsSchema) return true;
    return /\/ords(\/|$)/i.test(getApiBaseUrl());
}

function getLoginUrl() {
    if (typeof window === "undefined") return "/login";
    if (!useHashRouting()) return "/login";
    return `${window.location.pathname}${window.location.search}#/login`;
}

function isLoginLocation() {
    if (typeof window === "undefined") return false;
    if (useHashRouting()) {
        return (window.location.hash || "").startsWith("#/login");
    }
    return window.location.pathname === "/login";
}

function triggerForcedLogout(message) {
    const hadAuthenticatedUser = !!localStorage.getItem("user");
    if (!hadAuthenticatedUser) return;

    const explanation = message || "Authentication failed";
    const lastLoginMethod = localStorage.getItem("last_login_method");
    localStorage.clear();
    if (lastLoginMethod === "local" || lastLoginMethod === "oidc") {
        localStorage.setItem("last_login_method", lastLoginMethod);
    }
    localStorage.setItem("auth_message", `You were signed out: ${explanation}. Please log in again.`);

    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:logout", {detail: {message: explanation}}));
        if (!isLoginLocation()) {
            window.location.replace(getLoginUrl());
        }
    }
}

function hasRecentOidcLoginAttempt() {
    const raw = localStorage.getItem("oidc_login_started_at_ms");
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return false;
    return (Date.now() - value) < 120000;
}

function isOidcLoginInProgress() {
    if (typeof sessionStorage === "undefined") return false;
    return sessionStorage.getItem("oidc_login_in_progress") === "1";
}

function handleAuthenticationFailure(path, errorPayload) {
    const isAuthFailure = errorPayload && errorPayload.status === 403;
    const isLoginRequest = path === "/login";
    const isLogoutRequest = path === "/logout";
    const isOidcCallbackRequest = path.startsWith("/oidc/callback");
    const inOidcFinalizeWindow = isOidcLoginInProgress() || hasRecentOidcLoginAttempt();
    if (!isAuthFailure || isLoginRequest || isLogoutRequest || isOidcCallbackRequest || inOidcFinalizeWindow) return;
    triggerForcedLogout(errorPayload.message || "Not authenticated");
}

function handleAuthenticationNetworkFailure(path, error) {
    // Network hiccups are handled by the periodic session refresh logic in App.vue.
    // Keep immediate forced logout only for explicit 403 responses.
    return;
}

function request(path, options){
    options = options || {};
    const timeoutMs = options.timeoutMs || 5000;
    delete options.timeoutMs;
    options.credentials = "include";
    options.cache = "no-store";
    options.headers = options.headers || {};
    if (options.body){
        options.headers["Content-Type"] = "application/json";
    }

    let controller = null;
    let timeoutHandle = null;
    if (timeoutMs > 0 && typeof AbortController !== "undefined") {
        controller = new AbortController();
        options.signal = controller.signal;
        timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);
    }

    return fetch(getApiBaseUrl() + path, options)
        .then(res => {
            const contentType = (res.headers.get("content-type") || "").toLowerCase();
            const parsePayload = contentType.includes("application/json")
                ? res.json().catch(() => null)
                : res.text().then(text => text ? {message: text.trim()} : null);

            return parsePayload.then(payload => {
                if (!res.ok){
                    const errorPayload = (payload && typeof payload === "object") ? payload : {};
                    if (!errorPayload.message) {
                        errorPayload.message = `Request failed (${res.status})`;
                    }
                    errorPayload.status = res.status;
                    handleAuthenticationFailure(path, errorPayload);
                    return Promise.reject(errorPayload);
                }

                if (payload == null) return {success: true};
                return payload;
            });
        })
        .catch(error => {
            // HTTP errors are already handled above; do not treat them as network/auth failures.
            if (error && typeof error.status === "number") {
                return Promise.reject(error);
            }
            if (error && error.name === "AbortError") {
                return Promise.reject({
                    message: "Request timed out"
                });
            }
            handleAuthenticationNetworkFailure(path, error);
            return Promise.reject(error);
        })
        .finally(() => {
            if (timeoutHandle) clearTimeout(timeoutHandle);
        });
}

const login = (email, password) => request("/login", {
    method: "POST",
    body: JSON.stringify({email, password})
});
const getAuthConfig = () => request("/auth-config");
const getOidcStart = () => request("/oidc/start");
const completeOidcLogin = (code, state, redirectUri) => {
    if (isOrdsDeployment()) {
        return request(
            "/oidc/callback/"
            + encodeURIComponent(code)
            + "/"
            + encodeURIComponent(state)
            + "/frontend"
        );
    }
    return request(
        "/oidc/callback"
        + "?code=" + encodeURIComponent(code)
        + "&state=" + encodeURIComponent(state)
        + "&redirect_uri=" + encodeURIComponent(redirectUri)
    );
};
const buildOidcBrowserRedirectUrl = (code, state, redirectUri) => (
    getApiBaseUrl()
    + "/oidc/callback?browser_redirect=1"
    + "&code=" + encodeURIComponent(code)
    + "&state=" + encodeURIComponent(state)
    + "&redirect_uri=" + encodeURIComponent(redirectUri)
);
const changePassword = (oldPassword, newPassword) => request("/password", {
    method: "PATCH",
    body: JSON.stringify({oldPassword, newPassword})
});
const logout = () => request("/logout");
const getSession = () => request("/session", {timeoutMs: 10000});

const getBiobank = id => request("/biobanks/" + id);
const patchBiobank = biobank => request("/biobanks/" + biobank.id, {
    method: "PATCH",
    body: JSON.stringify(biobank)
});

const getCollection = id => request("/collections/" + id);
const getCollectionFactDerivationIssues = id => request("/collections/" + id + "/fact-derivation-issues");
const getCollectionsByBiobank = biobankId => request(
    "/collections?biobank=" + encodeURIComponent(biobankId)
);
const getCollectionsByParent = parentCollectionId => request(
    "/collections?parent_collection=" + encodeURIComponent(parentCollectionId)
);
const getAlsoKnownByBiobank = biobankId => request(
    "/also-known?biobank_id=" + encodeURIComponent(biobankId)
);
const getAlsoKnownByCollection = collectionId => request(
    "/also-known?collection_id=" + encodeURIComponent(collectionId)
);
const getAlsoKnownByNetwork = networkId => request(
    "/also-known?network_id=" + encodeURIComponent(networkId)
);
const postCollection = collection => request("/collections/", {
    method: "POST",
    body: JSON.stringify(collection)
});
const patchCollection = collection => request("/collections/" + collection.id, {
    method: "PATCH",
    body: JSON.stringify(collection)
});
const deleteCollection = id => request("/collections/" + id, {method: "DELETE"});

const getFacts = collectionId => request("/facts?CollectionId=" + encodeURIComponent(collectionId));
const deleteFacts = collectionId => request("/delete-facts?CollectionId=" + encodeURIComponent(collectionId));

const getPeople = (options) => {
    options = options || {};
    const biobank = options.biobank;
    if (biobank) {
        return request("/people/?biobank=" + encodeURIComponent(biobank));
    }
    return request("/people/");
};
const getPerson = id => request("/people/" + id);
const postPerson = person => request("/people/", {
    method: "POST",
    body: JSON.stringify(person)
});
const patchPerson = person => request("/people/" + person.id, {
    method: "PATCH",
    body: JSON.stringify(person)
});
const deletePerson = id => request("/people/" + id, {method: "DELETE"});

const getAlsoKnown = id => request("/also-known/" + id);
const postAlsoKnown = alsoKnown => request("/also-known/", {
    method: "POST",
    body: JSON.stringify(alsoKnown)
});
const patchAlsoKnown = alsoKnown => request("/also-known/" + alsoKnown.id, {
    method: "PATCH",
    body: JSON.stringify(alsoKnown)
});
const deleteAlsoKnown = id => request("/also-known/" + id, {method: "DELETE"});


const getNetworks = () => request("/networks/");
const getNetwork = id => request("/networks/" + id);
const postNetwork = network => request("/networks/", {
    method: "POST",
    body: JSON.stringify(network)
});
const patchNetwork = network => request("/networks/" + network.id, {
    method: "PATCH",
    body: JSON.stringify(network)
});
const deleteNetwork = id => request("/networks/" + id, {method: "DELETE"});

const searchDiseaseTypes = query => request("/disease-types/?q=" + encodeURIComponent(query));
const getAdminUsers = () => request("/admin/users");
const createAdminUser = user => request("/admin/users", {
    method: "POST",
    body: JSON.stringify(user)
});
const patchAdminUser = (email, patch) => request("/admin/users/" + encodeURIComponent(email), {
    method: "PATCH",
    body: JSON.stringify(patch)
});
const deleteAdminUser = email => request("/admin/users/" + encodeURIComponent(email), {method: "DELETE"});

const getAdminBiobanks = () => request("/admin/biobanks");
const createAdminBiobank = biobank => request("/admin/biobanks", {
    method: "POST",
    body: JSON.stringify(biobank)
});
const setAdminBiobankPublished = (id, published) => request("/admin/biobanks/" + encodeURIComponent(id) + "/publish", {
    method: "PATCH",
    body: JSON.stringify({published})
});
const deleteAdminBiobank = id => request("/admin/biobanks/" + encodeURIComponent(id), {method: "DELETE"});

const getBiobankDomains = biobankId => request("/admin/biobanks/" + encodeURIComponent(biobankId) + "/domains");
const addBiobankDomain = (biobankId, domain) => request("/admin/biobanks/" + encodeURIComponent(biobankId) + "/domains", {
    method: "POST",
    body: JSON.stringify({domain})
});
const deleteBiobankDomain = (biobankId, domain) => request("/admin/biobanks/" + encodeURIComponent(biobankId) + "/domains/" + encodeURIComponent(domain), {method: "DELETE"});
const setActiveBiobank = biobank => request("/admin/active-biobank", {
    method: "POST",
    body: JSON.stringify({biobank})
});
const clearActiveBiobank = () => request("/admin/active-biobank", {
    method: "POST",
    body: JSON.stringify({biobank: null})
});

export {
    login, getAuthConfig, getOidcStart, completeOidcLogin, buildOidcBrowserRedirectUrl, isOrdsDeployment,
    logout, getSession, changePassword, getBiobank, patchBiobank, postPerson,
    getPeople, getPerson, patchPerson, deletePerson, postCollection,
    getCollection, getCollectionsByBiobank, getCollectionsByParent,
    getCollectionFactDerivationIssues,
    patchCollection, deleteCollection, searchDiseaseTypes, getNetworks,
    postNetwork, getNetwork, patchNetwork, deleteNetwork,
    getAlsoKnownByBiobank, getAlsoKnownByCollection, getAlsoKnownByNetwork,
    getAlsoKnown, postAlsoKnown, patchAlsoKnown, deleteAlsoKnown, getFacts, deleteFacts,
    getAdminUsers, createAdminUser, patchAdminUser, deleteAdminUser,
    getAdminBiobanks, createAdminBiobank, setAdminBiobankPublished, deleteAdminBiobank,
    getBiobankDomains, addBiobankDomain, deleteBiobankDomain, setActiveBiobank, clearActiveBiobank
};
